import { useEffect, useRef, useState } from 'react';

interface IntersectionObserverOptions {
  root?: Element | Document | null;
  rootMargin?: string;
  threshold?: number | number[];
  checkHeight?: boolean;
}

const useIntersectionObserver = (
  ids: string[],
  options: IntersectionObserverOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0.05,
    checkHeight: true,
  }
) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const intersectingElementsRef = useRef<Map<string, IntersectionObserverEntry>>(new Map());
  const [activeId, setActiveId] = useState<string | null>(null);

  const cleanupObserver = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    intersectingElementsRef.current.clear();
    setActiveId(null);
  };

  useEffect(() => {
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null);

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (elements.length > 0) {
      observerRef.current = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            intersectingElementsRef.current.set(entry.target.id, entry);
          } else {
            intersectingElementsRef.current.delete(entry.target.id);
          }
        });

        if (intersectingElementsRef.current.size > 0) {
          if (options.checkHeight) {
            const maxElement = Array.from(intersectingElementsRef.current.values()).reduce(
              (maxEntry, currentEntry) =>
                currentEntry.intersectionRect.y > maxEntry.intersectionRect.y
                  ? currentEntry
                  : maxEntry
            );
            setActiveId(maxElement.target.id);
          } else {
            const lastElement = Array.from(intersectingElementsRef.current.values()).pop();
            if (lastElement) setActiveId(lastElement.target.id);
          }
        } else {
          setActiveId(null);
        }
      }, options);

      elements.forEach((element) => observerRef.current?.observe(element));
    } else {
      cleanupObserver();
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [ids, options]);

  return activeId;
};

export default useIntersectionObserver;
